import { defineComponent as _defineComponent } from 'vue'
import { message } from "ant-design-vue"
import { storeToRefs } from "pinia"
import { computed, ref, watch } from "vue"

import sntIcon from "@/assets/icons/snt-icon.png"
import DescriptionItem from "@/components/Common/DescriptionItem.vue"
// Components
import ProductCode from "@/components/Common/ProductCode"
import { SearchedProduct } from "@/components/Common/SearchProducts/SearchProducts.vue"
import Thumbnail from "@/components/Common/Thumbnail"
import { conditions } from "@/consts/offerConditions"
import i18n from "@/i18n"
import { UpdateDraftPayload, useSyncDraftsStore } from "@/modules/sync/store/useSyncDraftsStore"
import { useSyncMetaStore } from "@/modules/sync/store/useSyncMetaStore"
import offersService from "@/services/offersService"
import getFormattedPrice from "@/utils/getFormattedPrice"
import getProductState from "@/utils/getProductState"

// Helper Types/Variables

export default /*#__PURE__*/_defineComponent({
  __name: 'SyncDraftsExpandedRow',
  props: {
  draft: {
    type: Object,
    required: true
  },

  isNeedAction: {
    type: Boolean,
    required: true
  },

  isLocked: {
    type: Boolean,
    required: true
  },

  // Default delivery
  delivery: {
    type: String,
    required: true
  },

  warranty: {
    type: [Number, String],
    required: true
  },

  conditionValue: {
    type: String,
    required: true
  },

  synchronizationUuid: {
    type: String,
    required: true
  }
},
  emits: ["changeProduct", "showApproveWindow", "offerDeleted"],
  setup(__props, { expose, emit }) {

const props = __props;



// End Helper Types/Variables

// Store & Route
const syncDraftsStore = useSyncDraftsStore()
const metaStore = useSyncMetaStore()

const { deliveryPolitics } = storeToRefs(metaStore)
// End Store & Route

// State
const deliveryModel = ref(props.delivery)
const stateModel = ref(props.conditionValue)
const warrantyModel = ref(props.warranty)
const fetchingRemoving = ref(false)
const fetchingUpdating = ref(false)
const warrantyFetching = ref(false)
const deliveryFetching = ref(false)
const stateFetching = ref(false)
const warrantyMinValue = ref(0)
const loading = ref(false)

// Errors
const deliveryError = ref("")
const stateError = ref("")
const warrantyError = ref("")
// End State

// Computed
const draftUuid = computed(() => props.draft.uuid)
const product = computed(() => props.draft.product)
const offer = computed(() => props.draft.offer)
const hasDraftErrors = computed(() => Object.keys(props.draft.errors).length > 0)
const deliveryPoliticsWithConfigs = computed(() =>
  deliveryPolitics.value.filter((politic: any) => politic.configs.length > 0)
)
const stateDescription = computed(() => {
  return (
    offer.value &&
    getProductState(offer.value.condition_key, offer.value.condition_value).description
  )
})
// const isStateChangeBlocked = computed(() => !!product.value && !product.value?.is_allowed_condition)
// End Computed

// Methods
// Called from refs
const productSelect = (product: SearchedProduct): void => {
  const { pCode } = product
  const payload = {
    product: pCode
  }

  fetchingUpdating.value = true
  handleUpdateDraft(payload)
    .catch(() => {
      message.error(i18n.t("synchronization.product_not_updated") as string)
    })
    .finally(() => {
      fetchingUpdating.value = false
    })
}
const checkWarranty = (value: any) => {
  if (value === "" || value === null || value === undefined) {
    return i18n.t("validations.required_field")
  }

  if (Number(value) < warrantyMinValue.value) {
    return i18n.tc("synchronization.min_num", 0)
  }

  if (String(value).length > 4) {
    return i18n.tc("validations.max", 4)
  }

  return ""
}
const handleUpdateDraft = (payload: UpdateDraftPayload) => {
  const { synchronizationUuid } = props

  return syncDraftsStore.updateDraft({
    payload,
    synchronizationUuid,
    draftUuid: draftUuid.value
  })
}
const handleChangeDelivery = (uuid: string) => {
  if (!uuid) {
    deliveryError.value = i18n.t("validations.required_field") as string
    return
  }

  deliveryError.value = ""

  deliveryFetching.value = true
  let serverErrors: any = {}

  handleUpdateDraft({ delivery: uuid })
    .catch((err: any) => {
      serverErrors = err?.response?.data || {}
      message.error(i18n.t("synchronization.delivery_not_updated") as string)
    })
    .finally(() => {
      deliveryFetching.value = false

      if (serverErrors && serverErrors.delivery) {
        deliveryError.value = serverErrors.delivery
      }
    })
}
const handleChangeState = (state: string) => {
  if (!state) {
    stateError.value = i18n.t("validations.required_field") as string
    return
  }

  stateError.value = ""

  const condition = conditions.find((item) => `${item.backKey}_${item.value}` === state)

  if (!condition) return

  const { backKey, backValue } = condition
  const payload = {
    condition_key: backKey,
    condition_value: backValue ? backValue : 1
  }

  let serverErrors: any = {}

  stateFetching.value = true
  handleUpdateDraft(payload)
    .catch((err: any) => {
      serverErrors = err?.response?.data || {}
      message.error(i18n.t("synchronization.state_not_updated") as string)
    })
    .finally(() => {
      stateFetching.value = false

      if (serverErrors && serverErrors.state) {
        stateError.value = serverErrors.state
      }
    })
}
const handleChangeWarranty = (value: number) => {
  // Validate locally first
  const error = checkWarranty(value)

  if (error) {
    warrantyError.value = error as string
    return
  }

  warrantyError.value = ""

  let serverErrors: any = {}
  warrantyFetching.value = true

  handleUpdateDraft({ warranty: value })
    .catch((err: any) => {
      serverErrors = err?.response?.data || {}
      message.error(i18n.t("synchronization.warranty_not_updated") as string)
    })
    .finally(() => {
      warrantyFetching.value = false

      if (serverErrors && serverErrors.warranty) {
        warrantyError.value = serverErrors.warranty
      }
    })
}
const deleteOffer = async () => {
  if (!offer.value || fetchingRemoving.value) return

  fetchingRemoving.value = true
  try {
    await offersService.deleteOffer(offer.value.uuid)
    emit("offerDeleted")
  } catch {
    message.error(i18n.t("synchronization.drafts_not_updated") as string)
  } finally {
    fetchingRemoving.value = false
  }
}
// End Methods

// Lifecycle
// Watchers
// Update local model if props change (though usually props -> initial value, but if external update happens...)
watch(
  () => props.delivery,
  (val) => {
    if (val !== deliveryModel.value) deliveryModel.value = val
  }
)
watch(
  () => props.conditionValue,
  (val) => {
    if (val !== stateModel.value) stateModel.value = val
  }
)
watch(
  () => props.warranty,
  (val) => {
    if (val !== warrantyModel.value) warrantyModel.value = val
  }
)

// watch(
//   isStateChangeBlocked,
//   (val) => {
//     if (val) {
//       stateModel.value = `new_1`
//     }
//   },
//   { immediate: true }
// )
// End Lifecycle

expose({
  productSelect
})

return { __sfc: true,props, emit, syncDraftsStore, metaStore, deliveryPolitics, deliveryModel, stateModel, warrantyModel, fetchingRemoving, fetchingUpdating, warrantyFetching, deliveryFetching, stateFetching, warrantyMinValue, loading, deliveryError, stateError, warrantyError, draftUuid, product, offer, hasDraftErrors, deliveryPoliticsWithConfigs, stateDescription, productSelect, checkWarranty, handleUpdateDraft, handleChangeDelivery, handleChangeState, handleChangeWarranty, deleteOffer, sntIcon, DescriptionItem, ProductCode, Thumbnail, conditions, getFormattedPrice }
}

})